import React, { useState, useEffect, useContext } from 'react';
import './styles/FullDetails.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { UserContext } from '../UserContext';
import { decodeId } from "../utils/encrypt";
import moment from 'moment';
import { useNavigate, useParams } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Scrollbar, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import { FaDownload, FaUsers, FaUser, FaPlane, FaFileAlt, FaBoxOpen } from 'react-icons/fa';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { IoMdArrowBack } from "react-icons/io";
import { openSecureFileUrl } from '../utils/fileAccess';

// Formatting default date
const formatDate = (date) => {
  if (!date) return 'NA';
  const temp = date.split('T')[0];
  return moment(temp).format('DD-MM-YYYY');
};

const getCurrentStageLabel = (currentStage) => {
  if (currentStage === 'MANAGER') return 'Pending Manager Approval';
  if (currentStage === 'ADMIN') return 'Pending Admin Approval';
  if (currentStage === 'MANAGER_REJECTED') return 'Rejected by Manager';
  if (currentStage === 'ADMIN_REJECTED') return 'Rejected by Admin';
  return currentStage || 'NA';
};

const getIndividualAgeLabel = (individual) => {
  if (individual?.age) return individual.age;
  if (!individual?.dob) return 'NA';

  const dob = new Date(individual.dob);
  if (Number.isNaN(dob.getTime())) return 'NA';

  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  const dayDiff = today.getDate() - dob.getDate();

  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age -= 1;
  }

  return age >= 0 ? `${age} Years` : 'NA';
};

export default function FullDetails() {
  const { token } = useContext(UserContext);
  const { requestId } = useParams();     // encrypted ID
  const realId = decodeId(requestId);    // decrypt
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    bookingDetails: {},
    individuals: [],
  });
  const [loadError, setLoadError] = useState('');
  useEffect(() => {
    if (!realId) {
      setLoadError("Invalid request link or encryption key mismatch.");
      return;
    }

    const fetchData = async () => {
      try {
        setLoadError('');
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/requests/${realId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          navigate("/login");
          throw new Error("Failed to fetch");
        }

        const data = await response.json();
        setFormData(data);
      } catch (error) {
        console.error("Fetch error:", error);
        setLoadError(error.message || "Failed to load request details.");
      }
    };

    fetchData();
  }, [realId, token]);

  const generatePDF = () => {
    const doc = new jsPDF();
    const booking = formData.bookingDetails || {};
    let yOffset = 10;
    const margin = 10;
    const pageHeight = doc.internal.pageSize.height;
    const pageWidth = doc.internal.pageSize.width;
    const maxTextHeight = 10;
    const sectionHeaderHeight = 15;
    const labelMargin = 5;

    const calculateLabelWidth = (labels) => {
      let maxWidth = 0;
      labels.forEach(label => {
        const width = doc.getTextWidth(label);
        if (width > maxWidth) maxWidth = width;
      });
      return maxWidth;
    };

    const maxLabelWidth = calculateLabelWidth([
      'Request ID:', 'Batch Id:', 'Center:', 'Client:', 
      'Cost Center:', 'Support Needed:', 'Location:', 
      'Date:', 'Requester Remarks', 'Passengers Total Count:', 
      'Passenger 1:', 'Name:', 'Type:', 'Purpose:', 
      'Project:', 'Vertical:', 'ID Type:', 'ID Number:', 'Status:', 'Booking Date:', 
      'Booking By:', 'Amount:', 'PNR Number:', 
      'Booking Status:', 'Reimbursement Status:', 'Remarks:',
      'Self Booked:', 'Ticket Bill:', 'Accommodation Bill:',
      'Travel Preference:', 'Travel Class:', 'Train Number:',
      'Train Timing:', 'Bus Name:', 'Bus Timing:',
      'Reimbursement Approved By:', 'Reimbursement Approved Date:',
      'Payment Proof:',
     
    ]) + labelMargin;

    const addText = (label, value, isBold = false) => {
      if (yOffset + maxTextHeight > pageHeight - margin) {
        doc.addPage();
        yOffset = margin;
      }

      doc.setFont("Helvetica", isBold ? "bold" : "normal");

      const labelText = `${label}:`;
      doc.text(labelText, margin, yOffset);

      const valueX = margin + maxLabelWidth;
      const availableWidth = pageWidth - valueX - margin;
      const valueLines = doc.splitTextToSize(value, availableWidth);

      valueLines.forEach((line) => {
        if (yOffset + maxTextHeight > pageHeight - margin) {
          doc.addPage();
          yOffset = margin;
        }
        doc.text(line, valueX, yOffset);
        yOffset += maxTextHeight;
      });
    };

    const addLink = (label, linkText, url) => {
      if (yOffset + maxTextHeight > pageHeight - margin) {
        doc.addPage();
        yOffset = margin;
      }

      const linkX = margin + maxLabelWidth;
      const availableWidth = pageWidth - linkX - margin;
      const linkTextLines = doc.splitTextToSize(linkText, availableWidth);

      doc.setFont("Helvetica", "normal");
      doc.setTextColor(0, 0, 0);
      doc.text(`${label}:`, margin, yOffset);

      doc.setTextColor(0, 0, 255);

      linkTextLines.forEach((line) => {
        if (yOffset + maxTextHeight > pageHeight - margin) {
          doc.addPage();
          yOffset = margin;
        }
        doc.textWithLink(line, linkX, yOffset, { url });
        yOffset += maxTextHeight;
      });

      doc.setTextColor(0, 0, 0);
    };

    const addSectionHeader = (header) => {
      yOffset += 5;
      if (yOffset + sectionHeaderHeight > pageHeight - margin) {
        doc.addPage();
        yOffset = margin;
      }
      addText(header, '', true);
    };

    const addSeparator = () => {
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, yOffset, pageWidth - margin, yOffset);
      yOffset += 5;
    };

    yOffset += 5;
    doc.setFontSize(16);
    addText(`Request #${realId}`, '', true);
    addSeparator();
    doc.setFontSize(12);

    addSectionHeader("Request Details");
    addText("Request ID", realId.toString());
    addText("Batch Id", formData.batchId || 'NA');
    addText("Center", formData.center || 'NA');
    addText("Client", formData.client || 'NA');
    addText("Cost Center", formData.costCenter || 'NA');
    addText("Purpose", formData.purpose || 'NA');
    addText("Support Needed", formData.supportNeeded || 'NA');

    if (formData.supportNeeded === "Travel") {
      addText("Trip Type", formData.tripType || "One Way");

      addText("Going Source", formData.source || "NA");
      addText("Going Destination", formData.destination || "NA");
      addText("Going Travel Preference", formData.travelPreference || "NA");
      addText("Going Travel Class", formData.travelClass || "NA");

      if (formData.travelPreference === 'Train') {
        addText("Going Train Number", formData.trainNumber || 'NA');
        addText("Going Train Timing", formData.trainTiming || 'NA');
      }

      if (formData.travelPreference === 'Bus') {
        addText("Going Bus Name", formData.busName || 'NA');
        addText("Going Bus Timing", formData.busTiming || 'NA');
      }

      addText("Going Journey Date", formatDate(formData.fromDate));

      if (formData.tripType === "Round Trip") {
        addText("Return Source", formData.returnSource || "NA");
        addText("Return Destination", formData.returnDestination || "NA");
        addText("Return Travel Preference", formData.returnTravelPreference || "NA");
        addText("Return Travel Class", formData.returnTravelClass || "NA");

        if (formData.returnTravelPreference === 'Train') {
          addText("Return Train Number", formData.returnTrainNumber || 'NA');
          addText("Return Train Timing", formData.returnTrainTiming || 'NA');
        }

        if (formData.returnTravelPreference === 'Bus') {
          addText("Return Bus Name", formData.returnBusName || 'NA');
          addText("Return Bus Timing", formData.returnBusTiming || 'NA');
        }

        addText("Return Journey Date", formatDate(formData.returnDate));
      }
    } else {
      addText("Source", formData.source || 'NA');
      addText("Destination", formData.destination || 'NA');
      addText(
        "Date",
        `${formatDate(formData.fromDate)} to ${formatDate(formData.toDate)}`
      );
    }
    addText("Requester Remarks", formData.requesterRemarks || "NA");
    addText("Self Booked", booking.selfBooked ? "Yes" : "No");
    if (booking.selfBooked) {
      addText("Reimbursement Status", booking.reimbursementStatus || "Pending");
    } else {
      addText("Booking Status", booking.bookingStatus || "NA");
    }
    if (booking.selfBooked) {
      addText("Ticket Amount", booking.selfBookedTicketAmount || "NA");
      addText("Accommodation Details", booking.selfBookedHotelDetails || "NA");
      addText("Accommodation Amount", booking.selfBookedHotelAmount || "NA");
      if (booking.paymentProof) {
        const parts = booking.paymentProof.split('$');
        const proofName = parts[0] || 'Payment Proof';
        const proofUrl = parts[1] || '';
        if (proofUrl) {
          addLink("Payment Proof", proofName, proofUrl);
        } else {
          addText("Payment Proof", proofName);
        }
      } else {
        addText("Payment Proof", "NA");
      }
      if ((booking.reimbursementStatus || "Pending") === "Approved") {
        addText("Reimbursement Approved By", booking.reimbursementApprovedBy || "NA");
        addText("Reimbursement Approved Date", formatDate(booking.reimbursementApprovedDate));
      } else if ((booking.reimbursementStatus || "Pending") === "Rejected") {
        addText("Reimbursement Rejected By", booking.reimbursementRejectedBy || "NA");
        addText("Reimbursement Rejected Date", formatDate(booking.reimbursementRejectedDate));
        
      }
    }

    doc.save(`Request_${realId}.pdf`);
  };

  const handleViewFile = async (event, fileValue) => {
    event.preventDefault();
    try {
      await openSecureFileUrl(fileValue, token);
    } catch (error) {
      console.error('Failed to open secure file:', error);
    }
  };

  const isCourier = formData.supportNeeded?.includes("Courier");
  const statusPillClass = `status-pill status-${String(formData.status || 'Pending').toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <div className='Full-Details'>
      <Header />
      <main className='main-content'>
        {loadError ? (
          <div className="section-container">
            <div className="section-header">
              <div className="back-icon" onClick={() => navigate(-1)}>
                <IoMdArrowBack />
              </div>
              Request Details
            </div>
            <div className="section-body">
              <div className="detail-row"><strong>{loadError}</strong></div>
            </div>
          </div>
        ) : (
        <>
        <div className='download-container'>
          <button className='download-button' onClick={generatePDF}>
            <FaDownload />
            <span>Download as PDF</span>
          </button>
        </div>
        <Swiper
  modules={[Navigation, Pagination, Scrollbar, A11y]}
  spaceBetween={50}
  slidesPerView={1}
  navigation={true}
  pagination={{ clickable: true }}
  scrollbar={{ draggable: true }}
  className="Swiper"
>

  <SwiperSlide>
    <div className="full-details-overlay visible" onClick={() => navigate(-1)}></div>

    <div className="section-container">
      <div className="section-header">
        <div className="back-icon" onClick={() => navigate(-1)}>
          <IoMdArrowBack />
        </div>
        Request Details
      </div>

      <div className="section-body">
        <div className="detail-block">
          <div className="detail-block__header">
            <span className="detail-block__icon"><FaFileAlt /></span>
            <div className="detail-block__title-group">
              <h3 className="detail-block__title">Request Information</h3>
            </div>
          </div>
          <div className="detail-block__body">
            <div className="detail-row"><strong>Request ID:</strong><span>{realId}</span></div>
            <div className="detail-row">
              <strong>Status:</strong>
              <span className={statusPillClass}>
                <span className="status-dot" />{formData.status || 'Pending'}
              </span>
            </div>
            <div className="detail-row"><strong>Branch:</strong><span>{formData.branch || 'NA'}</span></div>
            <div className="detail-row"><strong>Center:</strong><span>{formData.center || 'NA'}</span></div>
            <div className="detail-row"><strong>Client:</strong><span>{formData.client || 'NA'}</span></div>
            <div className="detail-row"><strong>Cost Center:</strong><span>{formData.costCenter || 'NA'}</span></div>
            <div className="detail-row"><strong>Purpose:</strong><span>{formData.purpose || 'NA'}</span></div>
          </div>
        </div>

        <div className="detail-block">
          <div className="detail-block__header">
            <span className="detail-block__icon">{isCourier ? <FaBoxOpen /> : <FaPlane />}</span>
            <div className="detail-block__title-group">
              <h3 className="detail-block__title">{isCourier ? 'Shipment Details' : 'Journey Details'}</h3>
            </div>
          </div>
          <div className="detail-block__body">
            <div className="detail-row"><strong>Support Needed:</strong><span>{formData.supportNeeded || 'NA'}</span></div>
            {isCourier ? (
              <>
                <div className="detail-row"><strong>Sender Name:</strong><span>{formData.sentFrom || "NA"}</span></div>
                <div className="detail-row"><strong>Receiver Name:</strong><span>{formData.receivedBy || "NA"}</span></div>

                <div className="detail-row"><strong>Source Address:</strong><span>{formData.source || "NA"}</span></div>
                <div className="detail-row"><strong>Destination Address:</strong><span>{formData.destination || "NA"}</span></div>

                <div className="detail-row"><strong>Receiver Email:</strong><span>{formData.receiverEmail || "NA"}</span></div>
                <div className="detail-row"><strong>POD Number:</strong><span>{formData.podNumber || "NA"}</span></div>
                <div className="detail-row"><strong>Courier Company:</strong><span>{formData.courierCo || "NA"}</span></div>
              </>
            ) : (
              <>
                <div className="detail-row"><strong>Trip Type:</strong><span>{formData.tripType || "One Way"}</span></div>

                <div className="detail-row"><strong>Going Source:</strong><span>{formData.source || "NA"}</span></div>
                <div className="detail-row"><strong>Going Destination:</strong><span>{formData.destination || "NA"}</span></div>
                <div className="detail-row"><strong>Going Travel Preference:</strong><span>{formData.travelPreference || "NA"}</span></div>
                <div className="detail-row"><strong>Going Travel Class:</strong><span>{formData.travelClass || "NA"}</span></div>

                {formData.travelPreference === 'Train' && (
                  <>
                    <div className="detail-row"><strong>Going Train Number:</strong><span>{formData.trainNumber || "NA"}</span></div>
                    <div className="detail-row"><strong>Going Train Timing:</strong><span>{formData.trainTiming || "NA"}</span></div>
                  </>
                )}

                {formData.travelPreference === 'Bus' && (
                  <>
                    <div className="detail-row"><strong>Going Bus Name:</strong><span>{formData.busName || "NA"}</span></div>
                    <div className="detail-row"><strong>Going Bus Timing:</strong><span>{formData.busTiming || "NA"}</span></div>
                  </>
                )}

                {formData.tripType === "Round Trip" && (
                  <>
                    <div className="detail-row"><strong>Return Source:</strong><span>{formData.returnSource || "NA"}</span></div>
                    <div className="detail-row"><strong>Return Destination:</strong><span>{formData.returnDestination || "NA"}</span></div>
                    <div className="detail-row"><strong>Return Travel Preference:</strong><span>{formData.returnTravelPreference || "NA"}</span></div>
                    <div className="detail-row"><strong>Return Travel Class:</strong><span>{formData.returnTravelClass || "NA"}</span></div>

                    {formData.returnTravelPreference === 'Train' && (
                      <>
                        <div className="detail-row"><strong>Return Train Number:</strong><span>{formData.returnTrainNumber || "NA"}</span></div>
                        <div className="detail-row"><strong>Return Train Timing:</strong><span>{formData.returnTrainTiming || "NA"}</span></div>
                      </>
                    )}

                    {formData.returnTravelPreference === 'Bus' && (
                      <>
                        <div className="detail-row"><strong>Return Bus Name:</strong><span>{formData.returnBusName || "NA"}</span></div>
                        <div className="detail-row"><strong>Return Bus Timing:</strong><span>{formData.returnBusTiming || "NA"}</span></div>
                      </>
                    )}
                  </>
                )}
              </>
            )}

            {formData.supportNeeded === "Travel" ? (
              <>
                <div className="detail-row">
                  <strong>Going Journey Date:</strong>
                  <span>{formatDate(formData.fromDate)}</span>
                </div>

                {formData.tripType === "Round Trip" && (
                  <div className="detail-row">
                    <strong>Return Journey Date:</strong>
                    <span>{formatDate(formData.returnDate)}</span>
                  </div>
                )}
              </>
            ) : (
              <div className="detail-row">
                <strong>Date:</strong>
                <span>{formatDate(formData.fromDate)} to {formatDate(formData.toDate)}</span>
              </div>
            )}
            <div className="detail-row"><strong>Requester Remarks:</strong><span>{formData.requesterRemarks || 'NA'}</span></div>
          </div>
        </div>
      </div>
    </div>
  </SwiperSlide>

<SwiperSlide>
  <div className="section-container">
    <div className="section-header">
      <div className="back-icon" onClick={() => navigate(-1)}>
        <IoMdArrowBack />
      </div>
      Passengers Details
    </div>

    <div className="section-body passenger-body">
      <div className="passenger-count-summary">
        <div className="summary-icon"><FaUsers /></div>
        <div className="summary-copy">
          <span className="summary-label">Total Passengers</span>
          <strong className="summary-value">{formData.individuals.length}</strong>
        </div>
      </div>
      {formData.individuals.length > 0 ? (
        formData.individuals.map((individual, index) => (
            <div className="passenger-details" key={index}>
              <div className="passenger-card-heading">
                <span className="passenger-avatar"><FaUser /></span>
                <strong>Passenger {index + 1}</strong>
                <span className="passenger-index">{index + 1} of {formData.individuals.length}</span>
              </div>
            <div className="detail-row">
              <strong>Name:</strong> <span>{individual.name || 'NA'}</span>
            </div>
            <div className="detail-row">
              <strong>Contact Number:</strong>{' '}
              <span>{individual.contactNumber || 'NA'}</span>
            </div>
            <div className="detail-row">
              <strong>Type:</strong> <span>{individual.type || 'NA'}</span>
            </div>
            <div className="detail-row">
              <strong>ID Type:</strong> <span>{individual.idType || 'NA'}</span>
            </div>
            <div className="detail-row">
              <strong>Age:</strong> <span>{getIndividualAgeLabel(individual)}</span>
            </div>
            <div className="detail-row">
              <strong>ID Number:</strong> <span>{individual.idNumber || 'NA'}</span>
            </div>
            <div className="detail-row">
              <strong>Project:</strong> <span>{individual.project || 'NA'}</span>
            </div>
            <div className="detail-row">
              <strong>Vertical:</strong> <span>{individual.vertical || 'NA'}</span>
            </div>
          </div>
        ))
      ) : (
        <p>No Passenger Details Available</p>
      )}
    </div>
  </div>
</SwiperSlide>

  <SwiperSlide>
    <div className="section-container">
      <div className="section-header">
        <div className="back-icon" onClick={() => navigate(-1)}>
          <IoMdArrowBack />
        </div>
        Status Details
      </div>

      <div className="section-body status-body">
        <div className="status-overview">
          <span className="summary-icon"><FaFileAlt /></span>
          <div className="status-overview__col">
            <span className="summary-label">Status</span>
            <span className={statusPillClass}>
              <span className="status-dot" />{formData.status || 'Pending'}
            </span>
          </div>
          <span className="status-overview__divider" />
          <div className="status-overview__col">
            <span className="summary-label">Current Stage</span>
            <span className="stage-pill"><span className="stage-clock">◷</span>{getCurrentStageLabel(formData.currentStage)}</span>
          </div>
        </div>

        <div className="detail-block">
          <div className="detail-block__header">
            <span className="detail-block__icon"><FaFileAlt /></span>
            <div className="detail-block__title-group">
              <h3 className="detail-block__title">Approval &amp; Status History</h3>
            </div>
          </div>
          <div className="detail-block__body">
            <div className="detail-row"><strong>Manager Approved By:</strong><span>{formData.managerApprovedBy || 'NA'}</span></div>
            <div className="detail-row"><strong>Manager Approved At:</strong><span>{formatDate(formData.managerApprovedAt)}</span></div>
            <div className="detail-row"><strong>Admin Approved By:</strong><span>{formData.adminApprovedBy || 'NA'}</span></div>
            <div className="detail-row"><strong>Admin Approved At:</strong><span>{formatDate(formData.adminApprovedAt)}</span></div>
            <div className="detail-row"><strong>Declined By:</strong><span>{formData.declinedBy || 'NA'}</span></div>
            <div className="detail-row"><strong>Declined At:</strong><span>{formatDate(formData.declinedAt)}</span></div>
            <div className="detail-row"><strong>Decline Reason:</strong><span>{formData.declineReason || 'NA'}</span></div>
            <div className="detail-row">
              <strong>Admin Remarks:</strong>
              {formData.adminRemarks && formData.adminRemarks.length > 0 ? (
                <ul className="remarks-list">
                  {formData.adminRemarks.map((remark, index) => (
                    <li key={index}>{index + 1}) {remark}</li>
                  ))}
                </ul>
              ) : (
                <span>NA</span>
              )}
            </div>
            {formData.status === 'Cancelled' && (
              <>
                <div className="detail-row"><strong>Cancel Reason:</strong><span>{formData.bookingDetails.cancelReason || 'NA'}</span></div>
                <div className="detail-row">
                  <strong>Cancel Proof:</strong>
                  {formData.bookingDetails.cancelProof ? (
                    <span className="view-download">
                      <a
                        href={formData.bookingDetails.cancelProof.split('$')[1]}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => handleViewFile(e, formData.bookingDetails.cancelProof)}
                      >
                        {formData.bookingDetails.cancelProof.split('$')[0]}
                      </a>
                    </span>
                  ) : (
                    <span>NA</span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  </SwiperSlide>

  <SwiperSlide>
    <div className="section-container">
      <div className="section-header">
        <div className="back-icon" onClick={() => navigate(-1)}>
          <IoMdArrowBack />
        </div>
        Booking Details
      </div>

      <div className="section-body booking-body">
        <div className="detail-block">
          <div className="detail-block__header">
            <span className="detail-block__icon"><FaPlane /></span>
            <div className="detail-block__title-group">
              <h3 className="detail-block__title">Booking Information</h3>
              <span className="detail-block__subtitle">Booking and reimbursement overview</span>
            </div>
          </div>
          <div className="detail-block__body">
            {!formData.bookingDetails.selfBooked && (
              <>
                <div className="detail-row">
                  <strong>Booking Date:</strong>
                  <span>
                    {formData.bookingDetails.bookingDate
                      ? formatDate(formData.bookingDetails.bookingDate)
                      : 'NA'}
                  </span>
                </div>
                <div className="detail-row"><strong>Booking By:</strong><span>{formData.bookingDetails.bookingBy || 'NA'}</span></div>
                <div className="detail-row"><strong>Amount:</strong><span>{formData.bookingDetails.amount || 'NA'}</span></div>
                <div className="detail-row"><strong>PNR Number:</strong><span>{formData.bookingDetails.pnrNumber || 'NA'}</span></div>
              </>
            )}
            <div className="detail-row">
              <strong>{formData.bookingDetails.selfBooked ? 'Reimbursement Status' : 'Booking Status'}:</strong>
              <span className="stage-pill">
                {formData.bookingDetails.selfBooked
                  ? (formData.bookingDetails.reimbursementStatus || 'Pending')
                  : (formData.bookingDetails.bookingStatus || 'NA')}
              </span>
            </div>
            <div className="detail-row"><strong>Self Booked:</strong><span>{formData.bookingDetails.selfBooked ? 'Yes' : 'No'}</span></div>
            {formData.bookingDetails.selfBooked && (
              <>
                <div className="detail-row"><strong>Ticket Amount:</strong><span>{formData.bookingDetails.selfBookedTicketAmount || 'NA'}</span></div>
                <div className="detail-row"><strong>Accommodation Amount:</strong><span>{formData.bookingDetails.selfBookedHotelAmount || 'NA'}</span></div>
                {formData.bookingDetails.reimbursementStatus === 'Approved' && (
                  <>
                    <div className="detail-row"><strong>Reimbursement Approved By:</strong><span>{formData.bookingDetails.reimbursementApprovedBy || 'NA'}</span></div>
                    <div className="detail-row"><strong>Reimbursement Approved Date:</strong><span>{formatDate(formData.bookingDetails.reimbursementApprovedDate)}</span></div>
                  </>
                )}
                {formData.bookingDetails.reimbursementStatus === 'Rejected' && (
                  <>
                    <div className="detail-row"><strong>Reimbursement Rejected By:</strong><span>{formData.bookingDetails.reimbursementRejectedBy || 'NA'}</span></div>
                    <div className="detail-row"><strong>Reimbursement Rejected Date:</strong><span>{formatDate(formData.bookingDetails.reimbursementRejectedDate)}</span></div>
                  </>
                )}
                <div className="detail-row">
                  <strong>Ticket Details:</strong>
                  {formData.bookingDetails.selfBookedTicketBill ? (
                    <span className="view-download">
                      <a
                        href={formData.bookingDetails.selfBookedTicketBill.split('$')[1]}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => handleViewFile(e, formData.bookingDetails.selfBookedTicketBill)}
                      >
                        {formData.bookingDetails.selfBookedTicketBill.split('$')[0]}
                      </a>
                    </span>
                  ) : (
                    <span>NA</span>
                  )}
                </div>
                <div className="detail-row">
                  <strong>Accommodation Bill:</strong>
                  {formData.bookingDetails.selfBookedHotelBill ? (
                    <span className="view-download">
                      <a
                        href={formData.bookingDetails.selfBookedHotelBill.split('$')[1]}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => handleViewFile(e, formData.bookingDetails.selfBookedHotelBill)}
                      >
                        {formData.bookingDetails.selfBookedHotelBill.split('$')[0]}
                      </a>
                    </span>
                  ) : (
                    <span>NA</span>
                  )}
                </div>
                <div className="detail-row">
                  <strong>Payment Proof:</strong>
                  {formData.bookingDetails.paymentProof ? (
                    <span className="view-download">
                      <a
                        href={formData.bookingDetails.paymentProof.split('$')[1]}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => handleViewFile(e, formData.bookingDetails.paymentProof)}
                      >
                        {formData.bookingDetails.paymentProof.split('$')[0]}
                      </a>
                    </span>
                  ) : (
                    <span>NA</span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  </SwiperSlide>
</Swiper>
        </>
        )}

      </main>
      <Footer />
    </div>
  )
}